/** @jsxFrag React.Fragment */
import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link } from '@coursera/cds-core';

import LinkButton from 'bundles/authentication-common/components/LinkButton';
import { LEARNER_HELP_CENTER_LINK } from 'bundles/authentication-common/constants';
import {
  USER_NAME_MAX_LENGTH,
  USER_NAME_MIN_LENGTH,
  USER_PASSWORD_MAX_LENGTH,
  USER_PASSWORD_MIN_LENGTH,
} from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/authentication';

type ResponseMessageFactoryOutput = Record<string, any>;

export function responseMessageFactory(): ResponseMessageFactoryOutput {
  const locale = _t.getLocale();
  return {
    malformedEmailAddress: _t('The email address you entered is not valid.'),
    failedPasswordRequirements: (
      <>
        <div>{_t('There seems to be an issue with the password you entered. Please keep in mind the following:')}</div>
        <br />
        <ul>
          <li>
            {_t('Password must be between #{minLength} and #{maxLength} characters.', {
              minLength: USER_PASSWORD_MIN_LENGTH,
              maxLength: USER_PASSWORD_MAX_LENGTH,
            })}
          </li>
          <li>
            {_t(
              "Ensure your password is sufficiently complex. Avoid simple passwords like 'qwerty123' or 'password123'."
            )}
          </li>
        </ul>
        <FormattedMessage
          message={_t('Please try again, if problem persists visit the {helpCenterLink}.')}
          helpCenterLink={<Link href={`https://learner.coursera.help/hc/${locale}`}>{_t('Learner Help Center')}</Link>}
        />
      </>
    ),
    invalidName: _t(
      'Please enter your full name. It should be between #{minLength} and #{maxLength} characters. It should not be an email address or URL. Please avoid using authoritative titles.',
      {
        minLength: USER_NAME_MIN_LENGTH,
        maxLength: USER_NAME_MAX_LENGTH,
      }
    ),
    existingCourseraAccount: (link: () => void) => (
      <FormattedMessage
        message={_t('Looks like you already have an account with that email address. Please {loginLink} instead.')}
        loginLink={
          <LinkButton onClick={link} type="button">
            <strong>{_t('log in')}</strong>
          </LinkButton>
        }
      />
    ),
    success: _t('Signup success! Redirecting you now...'),
    successRedirect: _t('Signup success! You have been redirected.'),
  };
}

export const generalErrorMessage = () => (
  <FormattedMessage
    message="{error} {helpCenterLink}."
    error="An unexpected error has occurred. Please try again or contact our"
    helpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>{_t('Learner Help Center')}</a>}
  />
);

/* Start errors caught from BE: https://docs.google.com/spreadsheets/d/1YLMQa0o8dBDqRLxs1sHrjTWjjxC5ju8UaASC55SjANk/edit */
const existingEmailError = 'existingCourseraAccount';
const passwordRequirementsError = 'failedPasswordRequirements';
const nameRequirementsError = 'invalidName';
const emailRequirementsError = 'malformedEmailAddress';

export { existingEmailError, passwordRequirementsError, nameRequirementsError, emailRequirementsError };
